import * as T from 'three';
import type { RideTrack } from './model';
import type { RideCourse } from './course';
import { batchStatic, tube } from './bike';
import { metricUVs, surface, type Surface } from './render/materials';

export interface Clearing { x: number; z: number; radius: number }

/** Batched, decorative landmarks. Their footprints stay outside the riding corridor. */
export function makeArenaScenery(track: RideTrack, course: RideCourse, height: (x: number, z: number) => number) {
  const group = new T.Group(); group.name = `scenery-${track.biome}`;
  const clearings: Clearing[] = [];
  /** "kind:#colour" picks a photographic surface tinted by the colour; a bare colour is glossy paint. */
  const material = (spec: string) => { const [kind, color] = spec.includes(':') ? spec.split(':') as [Surface, string] : ['paint' as Surface, spec]; return surface(kind, color); };
  function box(parent: T.Group, color: string, size: [number, number, number], position: [number, number, number]) {
    const mesh = new T.Mesh(metricUVs(new T.BoxGeometry(...size)), material(color)); mesh.position.set(...position); mesh.castShadow = true; mesh.receiveShadow = true; parent.add(mesh); return mesh;
  }
  function cylinder(parent: T.Group, color: string, top: number, bottom: number, h: number, position: [number, number, number], sides = 10) {
    const mesh = new T.Mesh(metricUVs(new T.CylinderGeometry(top, bottom, h, Math.max(sides, 16))), material(color)); mesh.position.set(...position); mesh.castShadow = true; mesh.receiveShadow = true; parent.add(mesh); return mesh;
  }
  function place(u: number, side: number, offset: number, radius: number, build: (p: T.Group) => void) {
    const s = course.sample(u * track.length), x = s.x - s.tz * side * (track.width / 2 + offset), z = s.z + s.tx * side * (track.width / 2 + offset);
    // On a loop, the opposite straight also needs its full clear corridor.
    for (let d = 0; d <= track.length; d += 4) { const p = course.sample(d); if (Math.hypot(p.x - x, p.z - z) < track.width / 2 + radius + 2) return; }
    const p = new T.Group(); p.position.set(x, height(x, z), z); p.rotation.y = Math.atan2(s.tx, s.tz); build(p); group.add(p); clearings.push({ x, z, radius: radius + 3 });
  }
  if (track.biome === 'meadow') {
    for (const [i, u] of [.07, .37, .73].entries()) place(u, i % 2 ? -1 : 1, 27, 13, p => {
      box(p, 'concrete:#b8ab98', [17, 3, 13], [0, -.7, 0]);
      box(p, i % 2 ? 'wood:#e8d2a0' : 'wood:#c8563f', [16, 7, 12], [0, 3.5, 0]);
      const roof = cylinder(p, 'roof:#8a8178', 0, 12, 5, [0, 9, 0], 4); roof.rotation.y = Math.PI / 4; roof.scale.z = .8;
      for (const z of [-6.06, 6.06]) {
        box(p, 'wood:#f2ead8', [4.4, 5.4, .12], [0, 2.7, z]); box(p, 'wood:#6c5a48', [3.7, 4.8, .16], [0, 2.4, z]);
        for (const x of [-5.5, 5.5]) box(p, 'wood:#f2ead8', [2, 2.2, .12], [x, 4, z]);
      }
      for (const x of [-12, 12]) cylinder(p, 'fabric:#d8bd6b', 1.5, 1.5, 2, [x, 1, 3], 16).rotation.z = Math.PI / 2;
    });
    for (const u of [.16, .56, .9]) place(u, -1, 20, 7, p => {
      cylinder(p, 'plaster:#efe6cf', 1.6, 3, 10, [0, 5, 0]); cylinder(p, 'wood:#7a6048', 0, 2.4, 3, [0, 11.5, 0]);
      for (const angle of [Math.PI / 4, -Math.PI / 4]) { const sail = box(p, 'fabric:#f3e8c6', [.85, 13, .25], [0, 10, -2]); sail.rotation.z = angle; }
      box(p, 'wood:#6b5440', [1.1, 2.6, .12], [0, 1.3, -2.7]);
    });
  }
  if (track.biome === 'harbour') {
    for (const [i, u] of [.035, .2, .45, .66, .82].entries()) place(u, -1, 26, 15, p => {
      box(p, 'concrete:#a9aeb0', [20, 3, 16], [0, -.5, 0]); box(p, ['brick:#d9a48f','plaster:#ece0c6','metal:#9fb7c2'][i % 3], [20, 9, 14], [0, 4.5, 0]);
      const roof = cylinder(p, 'metal:#7d95a6', 0, 15, 4, [0, 11, 0], 4); roof.rotation.y = Math.PI / 4; roof.scale.z = .75;
      for (const z of [-7.08, 7.08]) for (const x of [-6, 0, 6]) box(p, 'glass:#2c3f48', [3.5, 4, .15], [x, 5, z]);
      box(p, 'metal:#5f8597', [5, 6, .2], [0, 3, -7.2]);
      for (const x of [-13, 13]) { box(p, 'metal:#3d6f8a', [2, 2, 2], [x, 1, 0]); box(p, 'metal:#e0a15c', [2, 1.3, 2], [x, 2.7, 0]); }
    });
    for (const u of [.08, .35, .6, .86]) place(u, 1, 4, 1, p => {
      cylinder(p, '#3a4a55', .09, .15, 7, [0, 3.5, 0]); box(p, '#3a4a55', [2.5, .12, .12], [1.1, 6.9, 0]);
      box(p, '#fff2c4', [.8, .2, .5], [2.2, 6.8, 0]);
    });
    const maxX = Math.max(...Array.from({length:401},(_,i)=>course.sample(track.length * i/400).x));
    for (const z of [-80, 0, 80]) {
      box(group, 'wood:#a08d74', [100, 1, 6], [maxX + 58, 14.6, z]);
      for (let x = 20; x < 105; x += 14) for (const side of [-1, 1]) cylinder(group, 'concrete:#8f989c', .3, .4, 12, [maxX + x, 9, z + side * 2.4]);
      for (const x of [37, 65, 93]) for (const side of [-1, 1]) {
        const boat = new T.Group(); boat.position.set(maxX + x, 14.6, z + side * 10);
        const hull = new T.Mesh(new T.SphereGeometry(1, 24, 12), material('#f4f1ea')); hull.scale.set(2, .8, 5.4); boat.add(hull);
        cylinder(boat, '#d9dfd9', .07, .07, 11, [0, 6, 0]);
        const sail = new T.BufferGeometry(); sail.setAttribute('position', new T.Float32BufferAttribute([0,2,0,0,10,0,0,2,4],3)); sail.computeVertexNormals();
        boat.add(new T.Mesh(sail, new T.MeshStandardMaterial({color:'#e5e9db',side:T.DoubleSide}))); group.add(boat);
      }
    }
    const lighthouse = new T.Group(); lighthouse.position.set(maxX + 34, 13.8, 115);
    cylinder(lighthouse, 'concrete:#a7aca8', 9, 12, 3, [0, 0, 0], 24);
    cylinder(lighthouse, 'plaster:#f6f1e6', 2.6, 4, 22, [0, 12, 0], 24); cylinder(lighthouse, 'plaster:#d0553f', 3.05, 3.35, 4, [0, 12, 0], 24);
    cylinder(lighthouse, '#3f505c', 4, 4, .6, [0, 23, 0], 24); cylinder(lighthouse, 'glass:#f8dfa2', 2.4, 2.4, 3, [0, 25, 0], 24); cylinder(lighthouse, '#b8442f', 0, 4, 3, [0, 28, 0], 24); group.add(lighthouse);
  }
  if (track.biome === 'park') {
    for (const [i, u] of [.04, .23, .47, .68, .87].entries()) place(u, i % 2 ? -1 : 1, 19, 11, p => {
      const color = i % 2 ? '#9c7dc9' : '#3ca49c';
      box(p, 'concrete:#a3a7a6', [16, 2.5, 13], [0, -.7, 0]);
      for (const x of [-7, 7]) for (const z of [-5, 5]) box(p, '#3e4a52', [.18, 5, .18], [x, 2.5, z]);
      const canopy = cylinder(p, `fabric:${color}`, 0, 11.5, 4, [0, 6.5, 0], 4); canopy.rotation.y = Math.PI / 4; canopy.scale.z = .8;
      box(p, 'wood:#e2cfa2', [7, .2, 2], [0, 1.2, 0]); for (const z of [-2, 2]) box(p, 'wood:#e2cfa2', [7, .3, .65], [0, .6, z]);
    });
    for (const u of [.1, .4, .78]) place(u, 1, 25, 13, p => {
      for (let row = 0; row < 4; row++) {
        box(p, 'concrete:#9ea4a8', [21, .4, 2], [0, 1 + row * 1.05, row * 2]);
        for (const x of [-9, 9]) box(p, 'concrete:#9ea4a8', [.22, 1 + row * 1.05, .22], [x, (1 + row * 1.05)/2, row * 2]);
        for (let seat = 0; seat < 12; seat++) box(p, row % 2 ? '#a78ed2' : '#64b8ac', [1.25, .25, .8], [-9 + seat * 1.65, 1.45 + row * 1.05, row * 2]);
      }
    });
    for (let i = 0; i < 22; i++) place(i / 22, i % 2 ? -1 : 1, 3.8, .8, p => {
      box(p, '#3e4a52', [.12, 5, .12], [0, 2.5, 0]); box(p, i % 2 ? 'fabric:#a78ed2' : 'fabric:#3ca49c', [1.15, 2.6, .06], [.6, 3.6, 0]);
    });
    // Festival bunting follows the verge, never crosses the riding line.
    for (const u of [.03, .51]) {
      const a = course.sample(u * track.length), b = course.sample((u + .045) * track.length), offset = track.width / 2 + 3.8;
      const pa = new T.Vector3(a.x - a.tz * offset, a.y + 5, a.z + a.tx * offset), pb = new T.Vector3(b.x - b.tz * offset, b.y + 5, b.z + b.tx * offset);
      group.add(tube(pa.toArray(), pb.toArray(), .03, material('fabric:#ece3ce')));
      for (let i = 0; i < 12; i++) {
        const p = pa.clone().lerp(pb, i/12), flag = new T.Mesh(new T.ConeGeometry(.65, 1.4, 3), material(i % 2 ? 'fabric:#e9bc74' : 'fabric:#9c7dc9')); flag.position.copy(p); flag.position.y -= .55; flag.rotation.z = Math.PI; group.add(flag);
      }
    }
  }
  // Flatten static transforms first so all landmarks share one draw call per material.
  group.updateMatrixWorld(true);
  const meshes: T.Mesh[] = []; group.traverse(o => { if (o instanceof T.Mesh) meshes.push(o); });
  for (const mesh of meshes) group.attach(mesh);
  for (const child of [...group.children]) if (child instanceof T.Group) group.remove(child);
  batchStatic(group);
  return { group, clearings };
}
